import { Box, Typography } from "@mui/material";
import { Markdown } from "@pimo/pimo-components";
import { FieldValues, get } from "react-hook-form";
import {
  AnswerType,
  APP_SOURCES,
  Configuration,
  FormBlock,
  FormQuestion,
  getEntityRefTarget,
} from "vmd-types";
import {
  getEntityRefFormKey,
  getEntityRefOptions,
  isEntityRefReadOnly,
  isQuestionVisible,
} from "vmd-utils";

import { SECONDARY_TEXT_COLOR, VMD_COLORS } from "../../constants/colors";
import { INFO_POPUP_CLASS } from "../../theme";
import { CUSTOM_ID_TYPEAHEAD_FIELD_TYPE } from "../custom-form-fields/custom-id-typeahead-field";
import { REPEATABLE_INPUT_FIELD_TYPE } from "../custom-form-fields/repeatable-input-field";
import { LlmQuestionLabel } from "./llm-question-label";
import { SourceQuestionLabel } from "./source-question-label";

/** Minimal duck-type that both FormTab and test doubles satisfy. */
export interface FieldSink {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addFormField: (field: any) => void;
}

const FIELD_TEXT_STYLE = {
  "& .MuiInputBase-input": { fontSize: "14px" },
  "& .MuiInputBase-inputMultiline, & textarea": { fontSize: "14px" },
  "& .MuiInputLabel-root": { fontSize: "14px" },
  "& .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    height: "100% !important",
    boxSizing: "border-box",
    alignItems: "flex-start",
  },
  fontSize: "14px",
} as const;

const EDITABLE_OUTLINE_SELECTORS = [
  "&.MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline",
  "&.MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline",
  "&.MuiOutlinedInput-root:not(.Mui-disabled).Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:not(.Mui-disabled).Mui-focused .MuiOutlinedInput-notchedOutline",
] as const;

const SUBMITTED_OUTLINE_SELECTORS = [
  "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline",
  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline",
  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline",
] as const;

const ERROR_OUTLINE_SELECTOR =
  "&.MuiOutlinedInput-root.Mui-error:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline";

function isEmptyFieldValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  );
}

function buildFieldStyle(
  fieldName: string,
  context: QuestionnaireFieldContext,
  disabled: boolean
) {
  if (disabled && !context.isAlreadySubmitted) return FIELD_TEXT_STYLE;

  const borderColor = context.isAlreadySubmitted
    ? "info.main"
    : context.dirtyFields && get(context.dirtyFields, fieldName)
      ? VMD_COLORS.yellow
      : isEmptyFieldValue(context.getValues?.(fieldName))
        ? VMD_COLORS.yellow
        : VMD_COLORS.green;
  const selectors = context.isAlreadySubmitted
    ? SUBMITTED_OUTLINE_SELECTORS
    : EDITABLE_OUTLINE_SELECTORS;

  return {
    ...FIELD_TEXT_STYLE,
    ...Object.fromEntries(
      selectors.map((selector) => [selector, { borderColor }])
    ),
    ...(context.isAlreadySubmitted
      ? {}
      : { [ERROR_OUTLINE_SELECTOR]: { borderColor: "error.main" } }),
  };
}

function isLlmSuggestedSource(source?: string): boolean {
  return (
    source?.trim().toLowerCase() === APP_SOURCES.LLM_SUGGESTED.toLowerCase()
  );
}

function buildIsVisible(
  question: FormQuestion,
  boundFormKeys?: Record<string, string>
) {
  if (!question.conditionalOn) return undefined;
  return (formValues: FieldValues) =>
    isQuestionVisible(
      question,
      formValues as Record<string, unknown>,
      boundFormKeys
    );
}

// Spans the whole blueprint: a condition's controlling question may sit in
// another block. `editableOnly` narrows it to what the questionnaire can write,
// which is what decides whether another tab must drop the same key.
export function buildBoundFormKeys(
  blocks: FormBlock[],
  { editableOnly = false }: { editableOnly?: boolean } = {}
): Record<string, string> {
  const keys: Record<string, string> = {};
  for (const block of blocks) {
    for (const question of block.questions ?? []) {
      const ref = question.entityRef;
      if (!ref || !getEntityRefTarget(ref)) continue;
      if (editableOnly && isEntityRefReadOnly(ref)) continue;
      keys[question.id] = getEntityRefFormKey(ref);
    }
  }
  return keys;
}

function requiredRule(question: FormQuestion) {
  if (!question.mandatory) return undefined;
  return { value: true, message: "This field is required" };
}

// The only fields still mandatory while creating a brand-new item: the
// Archer id (customId) and the item's name.
const CREATE_MODE_REQUIRED_FIELDS = new Set(["customId", "name"]);

/** Values a bound question needs that live outside the blueprint. */
export type QuestionnaireFieldContext = {
  configuration?: Configuration;
  /** Channel ids assigned to the OE, used to narrow channel targets. */
  oeChannelIds?: string[];
  /**
   * Columns a sibling tab already renders as mandatory. The last registration of
   * a name owns its rules, so a non-mandatory bound question would otherwise
   * clear the sibling's required rule.
   */
  requiredColumns?: string[];
  requiredMessage?: string;
  /** From `buildBoundFormKeys`; needed only by conditions on bound questions. */
  boundFormKeys?: Record<string, string>;
  /** Keys another tab renders; a read-only mirror here would freeze them. */
  skipFormKeys?: Set<string>;
  /** Question ids where the user has overridden an LLM-suggested answer. */
  overriddenLlmQuestionIds?: Set<string>;
  /** Baseline LLM values by question id (used to hide source immediately on edit). */
  initialLlmAnswersByQuestionId?: Record<string, unknown>;
  /** When true every field is rendered disabled (review already submitted). */
  forceReadOnly?: boolean;
  /** Reads a current form value by field name for field-status styling. */
  getValues?: (fieldName: string) => unknown;
  /** React Hook Form dirty fields, used to identify unsaved answers. */
  dirtyFields?: FieldValues;
  /** Whether this review has already been submitted. */
  isAlreadySubmitted?: boolean;
  /**
   * True while creating a brand-new item: only the customId-bound field is
   * required, every field is editable (even structurally read-only ones),
   * and the source badge is hidden since there is no source yet.
   */
  isCreateMode?: boolean;
  /**
   * True when this item was created manually by the current user (not via
   * import): unlocks every field the same way `isCreateMode` does, so the
   * creator can freely edit their own item despite readOnlyForOEs/read-only
   * entity-ref targets.
   */
  isOwnManualItem?: boolean;
  /**
   * Set when the currently open item was created manually (regardless of
   * who is viewing it now). Where a question would otherwise show a source
   * badge (`showSource`), this suppresses it instead: a manually-created
   * item has no meaningful per-question source, so nothing is shown here.
   * The provenance itself is still recorded on the item (source/createdBy)
   * for later stats/reporting, just not surfaced in this label.
   */
  manualItemCreatedBy?: string;
  /**
   * Replacement source labels, keyed by the configured source, applied only to
   * entity-ref-bound questions. An ADOIT-sourced question bound to a master
   * data column shows Archer data whenever the item has no ADOIT id, so the
   * badge has to follow the data instead of the blueprint.
   */
  entityRefSourceOverrides?: Record<string, string>;
  /** Overrides the default "already exists" message for the customId typeahead. */
  customIdDuplicateMessage?: string;
};

const PERCENTAGE_RANGE_MESSAGE = "Value must be between 0 and 100";

const NO_OPTIONS_SLOT = (
  <Box sx={{ p: 2 }}>
    <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
      No options configured yet
    </Typography>
  </Box>
);

function buildInfoSlot(question: FormQuestion) {
  if (!question.description) return undefined;
  return (
    // The class is what theme.ts targets to give the wrapper's Popover paper the
    // Pimo tooltip frame; `parent` keeps Markdown off its root-relative 1rem.
    <Box
      className={INFO_POPUP_CLASS}
      sx={{ maxWidth: 500, padding: "12px 16px" }}
    >
      <Typography
        component="div"
        sx={{ color: SECONDARY_TEXT_COLOR, fontSize: "14px", fontWeight: 400 }}
      >
        <Markdown fontSizeRelativeTo="parent">{question.description}</Markdown>
      </Typography>
    </Box>
  );
}

function mapAnswerTypeToField(
  question: FormQuestion,
  namePrefix: string,
  context: QuestionnaireFieldContext
): Record<string, unknown> {
  const isLlmSuggested = isLlmSuggestedSource(question.source);
  const target = question.entityRef
    ? getEntityRefTarget(question.entityRef)
    : undefined;
  // A saved blueprint can outlive the registry entry it points at, so a ref we no
  // longer know renders as an ordinary question instead of crashing the dialog.
  const ref = target ? question.entityRef : undefined;

  // The form key, not the column, is what collides: project targets are
  // namespaced and so share nothing with the Master Data tab.
  const inheritedRequired =
    ref && context.requiredColumns?.includes(getEntityRefFormKey(ref))
      ? {
          value: true,
          message: context.requiredMessage ?? "This field is required",
        }
      : undefined;

  const name = ref ? getEntityRefFormKey(ref) : `${namePrefix}.${question.id}`;
  // Creating a brand-new item, or editing one this same user manually
  // created, unlocks every field, including structurally read-only ones (OE
  // mirrors, channel targets): only an already-submitted review still forces
  // read-only rendering.
  const disabled =
    context.isCreateMode || context.isOwnManualItem
      ? Boolean(context.forceReadOnly)
      : Boolean(
          (question.readOnlyForOEs && !isLlmSuggested) ||
          (ref && isEntityRefReadOnly(ref) && !isLlmSuggested) ||
          context.forceReadOnly
        );
  // In create mode only the customId- and name-bound fields are mandatory;
  // every other field's required rule (admin-configured or inherited) is dropped.
  const requiredValue = context.isCreateMode
    ? CREATE_MODE_REQUIRED_FIELDS.has(name)
      ? {
          value: true,
          message: context.requiredMessage ?? "This field is required",
        }
      : undefined
    : (requiredRule(question) ?? inheritedRequired);

  const base = {
    name,
    label: (() => {
      const isMandatory = requiredValue;
      const hasPersistedOverride =
        isLlmSuggested &&
        Boolean(context.overriddenLlmQuestionIds?.has(question.id));
      const baseLabel = question.code
        ? `${question.code} – ${question.label}`
        : question.label;

      if (!isLlmSuggested) {
        // There is no source to show yet for a brand-new item; and a
        // manually-created item's fields have no meaningful per-question
        // source (LLM/OE/Archer etc.) of their own, so the badge stays
        // hidden for those too (the provenance is still recorded on the
        // item for later stats/reporting, just not surfaced here).
        if (
          question.showSource &&
          question.source &&
          !context.isCreateMode &&
          !context.manualItemCreatedBy
        ) {
          return (
            <SourceQuestionLabel
              baseLabel={baseLabel}
              isMandatory={Boolean(isMandatory)}
              source={
                (ref && context.entityRefSourceOverrides?.[question.source]) ||
                question.source
              }
            />
          );
        }
        return isMandatory ? `${baseLabel} *` : baseLabel;
      }

      if (hasPersistedOverride) {
        return isMandatory ? `${baseLabel} *` : baseLabel;
      }

      return (
        <LlmQuestionLabel
          baseLabel={baseLabel}
          fieldName={
            ref ? getEntityRefFormKey(ref) : `${namePrefix}.${question.id}`
          }
          isMandatory={Boolean(isMandatory)}
          question={question}
          hasPersistedOverride={hasPersistedOverride}
          initialLlmAnswer={
            context.initialLlmAnswersByQuestionId?.[question.id]
          }
        />
      );
    })(),
    sx: buildFieldStyle(name, context, disabled),
    required: requiredValue,
    isVisible: buildIsVisible(question, context.boundFormKeys),
    infoSlot: buildInfoSlot(question),
    uiOptions: { fieldSize: { sm: 12, xl: 6 }, equalRowHeight: true },
    ...(disabled ? { disabled: true, placeholder: "No value" } : {}),
  };

  // Create mode replaces the customId field with a typeahead that checks
  // for an existing Archer ID before the whole widget/options dispatch below,
  // since this field must stay a plain text input regardless of its target kind.
  if (context.isCreateMode && name === "customId") {
    return {
      ...base,
      type: CUSTOM_ID_TYPEAHEAD_FIELD_TYPE,
      duplicateMessage: context.customIdDuplicateMessage,
    };
  }

  // A bound question's widget and options follow its column, not the sheet's
  // Answer type, so a value the column cannot hold is never offered.
  if (ref && target) {
    const items = getEntityRefOptions(
      ref,
      context.configuration,
      context.oeChannelIds
    );
    const isMulti = target.kind === "stringArray" || target.kind === "channels";
    // A closed value set stays a select even when empty: free text would let a
    // typo reach a column the dashboards match by exact value.
    const isClosedSet = target.kind !== "text" && target.kind !== "longText";
    if (items.length === 0 && !isMulti && !isClosedSet) {
      // A bound value can be arbitrarily long regardless of its column's
      // "text" vs "longText" kind — an aggregated/derived value in particular
      // is never something the sheet owner sized the field for. Wrapping
      // instead of clipping to one line applies to both, not only the ones
      // the column happens to have marked "longText".
      return {
        ...base,
        type: "text",
        multiline: true,
        sx: {
          ...base.sx,
          "& .MuiInputBase-inputMultiline": {
            overflowY: "auto!important",
            maxHeight: 100,
          },
        },
      };
    }
    return {
      ...base,
      type: "dropdown",
      multiSelect: isMulti,
      menuItemsSx: FIELD_TEXT_STYLE,
      items,
      ...(isMulti ? { defaultValue: [] } : {}),
      // Nothing to choose from means the configuration has not loaded; leave the
      // stored value alone rather than offering an empty list to submit.
      ...(items.length === 0 ? { disabled: true } : {}),
    };
  }

  const type: AnswerType = question.answerType;

  switch (type) {
    case "text":
      return { ...base, type: "text", multiline: false };

    case "number":
      return { ...base, type: "number" };

    case "percentage":
      return {
        ...base,
        type: "number",
        startAdornment: "percentage",
        min: { value: 0, message: PERCENTAGE_RANGE_MESSAGE },
        max: { value: 100, message: PERCENTAGE_RANGE_MESSAGE },
      };

    case "date":
      return { ...base, type: "date" };

    case "textList":
      return { ...base, type: REPEATABLE_INPUT_FIELD_TYPE };

    case "environmentUrlList":
      return {
        ...base,
        type: REPEATABLE_INPUT_FIELD_TYPE,
        withEnvironment: true,
      };

    case "singleSelect": {
      const opts = (question.options ?? []).map((opt) => ({
        value: opt.id,
        label: opt.label,
      }));

      return {
        ...base,
        type: "dropdown",
        multiSelect: false,
        menuItemsSx: FIELD_TEXT_STYLE,
        items: opts,
        ...(opts.length === 0
          ? { disabled: true, infoSlot: NO_OPTIONS_SLOT }
          : {}),
      };
    }

    case "multiSelect": {
      const opts = (question.options ?? []).map((opt) => ({
        value: opt.id,
        label: opt.label,
      }));

      return {
        ...base,
        type: "dropdown",
        multiSelect: true,
        menuItemsSx: FIELD_TEXT_STYLE,
        items: opts,
        defaultValue: [],
        ...(opts.length === 0
          ? { disabled: true, infoSlot: NO_OPTIONS_SLOT }
          : {}),
      };
    }

    default: {
      // Fallback: render unknown answer types as plain text
      const _exhaustive: never = type;
      void _exhaustive;
      return { ...base, type: "text", multiline: false };
    }
  }
}

/**
 * Adds a form field for every enabled, ordered question in `block` to `tab`.
 * An unbound question is named `formData.<questionId>` so the submitted payload
 * nests it under `formData`. A question with an `entityRef` is named after its
 * master data column instead, so it reads and writes that column directly.
 *
 * @param tab         - the FormTab (or test double) to add fields to
 * @param block       - the FormBlock whose questions to render
 * @param namePrefix  - normally "formData"; override only in tests
 * @param context     - configuration and OE channels, needed by bound questions
 */
export function buildQuestionnaireBlockFields(
  tab: FieldSink,
  block: FormBlock,
  namePrefix = "formData",
  context: QuestionnaireFieldContext = {}
): void {
  const orderedQuestions = [...(block.questions ?? [])]
    .filter((q) => q.enabled)
    .sort((a, b) => a.order - b.order);

  // First pass: mark secondaries (non-bound questions whose label already appeared).
  // Only questions with a `source` (e.g. Archer) participate in deduplication;
  // plain user-defined questions are never collapsed even if they share a label.
  // entityRef questions are always primaries; their label is still tracked so a
  // subsequent sourced formData question with the same label is treated as a secondary.
  const secondaryIds = new Set<string>();
  const seenLabels = new Set<string>();
  for (const question of orderedQuestions) {
    const isRef = Boolean(
      question.entityRef && getEntityRefTarget(question.entityRef)
    );
    const normLabel = question.label.trim().toLowerCase();
    if (isRef) {
      seenLabels.add(normLabel);
    } else if (question.source && seenLabels.has(normLabel)) {
      secondaryIds.add(question.id);
    } else {
      seenLabels.add(normLabel);
    }
  }

  // When pairs are collapsed, renumber primaries sequentially so codes stay gapless.
  const codeOverrides = new Map<string, string>();
  if (secondaryIds.size > 0) {
    const primaries = orderedQuestions.filter((q) => !secondaryIds.has(q.id));
    const firstCoded = primaries.find((q) => q.code?.includes("."));
    const prefix = firstCoded?.code?.split(".")[0] ?? "";
    let seq = 1;
    for (const q of primaries) {
      if (q.code) {
        codeOverrides.set(q.id, prefix ? `${prefix}.${seq}` : String(seq));
        seq++;
      }
    }
  }

  for (const question of orderedQuestions) {
    if (secondaryIds.has(question.id)) continue;

    const ref =
      question.entityRef && getEntityRefTarget(question.entityRef)
        ? question.entityRef
        : undefined;
    if (ref && context.skipFormKeys?.has(getEntityRefFormKey(ref))) continue;

    const override = codeOverrides.get(question.id);
    const q = override ? { ...question, code: override } : question;
    tab.addFormField(mapAnswerTypeToField(q, namePrefix, context));
  }
}
